import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  DATA_E2E_KEY,
  IInjectedDrawerProps,
  IUserActionsHistoryDrawerProps,
  USER_ACTIONS_HISTORY_DRAWER_WIDTH_PX,
} from "@ryde/shared";

import { LayoutRightHeaderTitleStyled } from "../../../right";
import {
  DrawerCloseIconButton,
  DrawerContentStyled,
  MenuBaseDrawer,
  MenuDrawerHeaderStyled,
} from "../drawer";
import {
  UserActionsHistoryColumnStyled,
  UserActionsHistoryDrawerFooter,
  UserActionsHistoryPlaqueItem,
} from "./parts";
import { useUserActionsHistoryDrawer } from "./use-user-actions-history-drawer";
import {
  useUserActionsHistoryPlaqueDatetime,
  useUserActionsHistoryPlaqueNowMs,
} from "./use-user-actions-history-plaque-datetime";

export const UserActionsHistoryDrawer: FC<
  IUserActionsHistoryDrawerProps & IInjectedDrawerProps
> = ({ onCloseDrawer, drawerNumber }) => {
  const { t } = useTranslation();
  const formatPlaqueWhen = useUserActionsHistoryPlaqueDatetime();
  const plaqueNowMs = useUserActionsHistoryPlaqueNowMs();
  const {
    page,
    setPage,
    pageCount,
    slice,
    onClear,
    onClickHandler,
    hasUserContext,
    totalCards,
    listSurfaceRef,
  } = useUserActionsHistoryDrawer(onCloseDrawer);

  const emptyMessage = useMemo(() => {
    if (!hasUserContext) {
      return t("userActionsHistory.emptyNoUser", {
        defaultValue: "Sign in with a company to keep history.",
      });
    }
    return t("userActionsHistory.emptyCards", {
      defaultValue: "No opened cards yet.",
    });
  }, [hasUserContext, t]);

  return (
    <MenuBaseDrawer
      onCloseDrawer={onCloseDrawer}
      customWidth={`${USER_ACTIONS_HISTORY_DRAWER_WIDTH_PX}px`}
      drawerNumber={drawerNumber}
    >
      <MenuDrawerHeaderStyled>
        <LayoutRightHeaderTitleStyled className="DrawerHeaderTitle">
          {t("userActionsHistory.title", { defaultValue: "Recent activity" })}
        </LayoutRightHeaderTitleStyled>
        <DrawerCloseIconButton onCloseDrawer={onCloseDrawer} />
      </MenuDrawerHeaderStyled>

      <DrawerContentStyled className="user-actions-history-drawer-content">
        <UserActionsHistoryColumnStyled>
          <div
            ref={listSurfaceRef}
            className="UserActionsHistory-ListSurface"
            {...{ [DATA_E2E_KEY]: "user-actions-history-list-surface" }}
          >
            {!slice.length && (
              <p className="UserActionsHistory-Empty" role="status">
                {emptyMessage}
              </p>
            )}
            {slice.map(entry => (
              <UserActionsHistoryPlaqueItem
                key={entry.id}
                entry={entry}
                formatPlaqueWhen={formatPlaqueWhen}
                plaqueNowMs={plaqueNowMs}
                onClickHandler={onClickHandler}
              />
            ))}
          </div>
          <UserActionsHistoryDrawerFooter
            totalCards={totalCards}
            pageCount={pageCount}
            page={page}
            setPage={setPage}
            onClear={onClear}
          />
        </UserActionsHistoryColumnStyled>
      </DrawerContentStyled>
    </MenuBaseDrawer>
  );
};
